import { Box, Center, Container, Group, Stack, Text } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import type { OrganisationSummary } from '@/api/generated/models';
import { useSearchOrganisations } from '@/api/generated/organisations/organisations';
import {
  CardSection,
  ListPagination,
  MobileListFilters,
  PageHeading,
  SearchField,
  SelectField,
  StatusPill,
  TABLE_STATUS_COL_WIDTH,
  tableCenteredCellStyle,
  tableRowStyle
} from '@/components/common';
import { formatDateTime } from '@/utils/format';
import { usePageSize } from '@/utils/usePageSize';

const orgColumns: [string, number, ('center' | undefined)?][] = [
  ['platformAdmin.colOrganization', 260],
  ['platformAdmin.colStatus', TABLE_STATUS_COL_WIDTH, 'center'],
  ['adminOrganizations.colUsers', 100]
];

type StatusFilter = 'ALL' | 'ACTIVE' | 'SUSPENDED';

export const AdminOrganizationsPage = () => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = usePageSize();

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter, pageSize]);

  // staleTime: 0 — druga sesija (npr. suspenzija) može promijeniti stanje
  // bez signala natrag ovom ekranu, isti razlog kao ostale admin liste.
  const { data, isLoading } = useSearchOrganisations(
    {
      page,
      pageSize,
      search: debouncedSearch.trim() || undefined,
      isActive: statusFilter === 'ALL' ? undefined : statusFilter === 'ACTIVE'
    },
    { query: { staleTime: 0 } }
  );
  const pageItems = data?.content ?? [];
  const totalElements = data?.totalElements ?? 0;
  const totalPages = data?.totalPages ?? 1;

  const statusOptions = [
    { value: 'ALL', label: t('adminOrganizations.filterStatusAll') },
    { value: 'ACTIVE', label: t('platformAdmin.statusActive') },
    { value: 'SUSPENDED', label: t('platformAdmin.statusSuspended') }
  ];

  const activityLabel = (org: OrganisationSummary) =>
    formatDateTime(org.lastActivityAt) ?? t('adminOrganizations.noActivity');

  const paginationLabel = t('adminOrganizations.showing', {
    from: totalElements === 0 ? 0 : (page - 1) * pageSize + 1,
    to: Math.min(page * pageSize, totalElements),
    total: totalElements
  });

  return (
    <Container size="lg" px={0}>
      <Stack gap={24}>
        <PageHeading
          title={t('adminOrganizations.title')}
          subtitle={t('adminOrganizations.subtitle')}
        />

        <Group gap={16} wrap="wrap" align="flex-end" visibleFrom="sm">
          <div style={{ width: 280, maxWidth: '100%' }}>
            <SearchField
              hideLabel
              placeholder={t('adminOrganizations.searchPlaceholder')}
              value={search}
              onChange={(event) => setSearch(event.currentTarget.value)}
            />
          </div>
          <div style={{ width: 180 }}>
            <SelectField
              label={t('adminOrganizations.filterStatus')}
              data={statusOptions}
              value={statusFilter}
              onChange={(value) => setStatusFilter((value as StatusFilter) ?? 'ALL')}
              allowDeselect={false}
            />
          </div>
        </Group>

        <Box hiddenFrom="sm">
          <MobileListFilters
            search={
              <SearchField
                hideLabel
                placeholder={t('adminOrganizations.searchPlaceholder')}
                value={search}
                onChange={(event) => setSearch(event.currentTarget.value)}
              />
            }
            filters={[
              {
                key: 'status',
                label: t('adminOrganizations.filterStatus'),
                options: statusOptions,
                value: statusFilter,
                onChange: (value) => setStatusFilter((value as StatusFilter) ?? 'ALL'),
                emptyValue: 'ALL',
                variant: 'buttons'
              }
            ]}
          />
        </Box>

        <CardSection>
          <Box style={{ overflowX: 'auto' }} visibleFrom="sm">
            <div style={{ minWidth: 700 }}>
              {!isLoading && pageItems.length === 0 && (
                <Center py={40}>
                  <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
                    {t('adminOrganizations.emptyState')}
                  </Text>
                </Center>
              )}

              {pageItems.length > 0 && (
                <>
                  <div
                    className="table-header-row"
                    style={{
                      ...tableRowStyle,
                      padding: '12px 20px',
                      backgroundColor: 'var(--color-bg-canvas)',
                      borderBottom: '1px solid var(--color-border-soft)'
                    }}
                  >
                    {orgColumns.map(([key, width, align]) => (
                      <Text
                        key={key}
                        fz={10}
                        fw={600}
                        w={width}
                        ta={align}
                        style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4 }}
                      >
                        {t(key)}
                      </Text>
                    ))}
                    <Text
                      fz={10}
                      fw={600}
                      style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4, flex: 1 }}
                    >
                      {t('adminOrganizations.colLastActivity')}
                    </Text>
                    <Text
                      fz={10}
                      fw={600}
                      w={100}
                      ta="right"
                      style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4 }}
                    >
                      {t('platformAdmin.colAction')}
                    </Text>
                  </div>

                  {pageItems.map((org, index) => (
                    <div
                      key={org.id}
                      style={{
                        ...tableRowStyle,
                        padding: '14px 20px',
                        borderTop: index === 0 ? undefined : '1px solid var(--color-border-soft)'
                      }}
                    >
                      <Text
                        fz={13.5}
                        fw={600}
                        w={260}
                        style={{ color: 'var(--color-text-primary)' }}
                      >
                        {org.name}
                      </Text>
                      <div style={{ ...tableCenteredCellStyle, width: TABLE_STATUS_COL_WIDTH }}>
                        <StatusPill
                          label={t(
                            org.isActive
                              ? 'platformAdmin.statusActive'
                              : 'platformAdmin.statusSuspended'
                          )}
                          bg={
                            org.isActive
                              ? 'var(--color-status-success-soft)'
                              : 'var(--color-status-danger-soft)'
                          }
                          fg={
                            org.isActive
                              ? 'var(--color-status-success)'
                              : 'var(--color-status-danger)'
                          }
                        />
                      </div>
                      <Text fz={13.5} w={100} style={{ color: 'var(--color-text-primary)' }}>
                        {org.userCount}
                      </Text>
                      <Text fz={13} style={{ color: 'var(--color-text-muted)', flex: 1 }}>
                        {activityLabel(org)}
                      </Text>
                      <Text
                        component={Link}
                        to={`/admin/organizations/${org.id}`}
                        fz={12}
                        fw={600}
                        w={100}
                        ta="right"
                        style={{
                          color: 'var(--color-accent-primary-active)',
                          textDecoration: 'underline'
                        }}
                      >
                        {t('platformAdmin.detailsLink')}
                      </Text>
                    </div>
                  ))}

                  <ListPagination
                    label={paginationLabel}
                    page={page}
                    totalPages={totalPages}
                    onPageChange={setPage}
                    pageSize={pageSize}
                    onPageSizeChange={setPageSize}
                  />
                </>
              )}
            </div>
          </Box>
        </CardSection>

        <Stack gap={12} hiddenFrom="sm">
          {!isLoading && pageItems.length === 0 && (
            <Center py={40}>
              <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
                {t('adminOrganizations.emptyState')}
              </Text>
            </Center>
          )}

          {pageItems.map((org) => (
            <Stack
              key={org.id}
              gap={10}
              p={16}
              style={{
                width: '100%',
                borderRadius: 12,
                border: '1px solid var(--color-border-soft)',
                backgroundColor: 'var(--color-bg-surface)'
              }}
            >
              <Group justify="space-between" align="flex-start" wrap="nowrap">
                <Text fz={13.5} fw={600} style={{ color: 'var(--color-text-primary)' }}>
                  {org.name}
                </Text>
                <StatusPill
                  label={t(
                    org.isActive ? 'platformAdmin.statusActive' : 'platformAdmin.statusSuspended'
                  )}
                  bg={
                    org.isActive
                      ? 'var(--color-status-success-soft)'
                      : 'var(--color-status-danger-soft)'
                  }
                  fg={org.isActive ? 'var(--color-status-success)' : 'var(--color-status-danger)'}
                />
              </Group>
              <Group gap={12} wrap="wrap">
                <Text fz={11} style={{ color: 'var(--color-text-secondary)' }}>
                  <Text span fz={11} fw={600} style={{ color: 'var(--color-text-muted)' }}>
                    {t('adminOrganizations.colUsers')}:
                  </Text>{' '}
                  {org.userCount}
                </Text>
                <Text fz={11} style={{ color: 'var(--color-text-secondary)' }}>
                  <Text span fz={11} fw={600} style={{ color: 'var(--color-text-muted)' }}>
                    {t('adminOrganizations.colActivityMobile')}:
                  </Text>{' '}
                  {activityLabel(org)}
                </Text>
              </Group>
              <Box pt={10} style={{ borderTop: '1px solid var(--color-border-soft)' }}>
                <Text
                  component={Link}
                  to={`/admin/organizations/${org.id}`}
                  fz={12}
                  fw={600}
                  ta="right"
                  style={{
                    color: 'var(--color-accent-primary-active)',
                    textDecoration: 'underline'
                  }}
                >
                  {t('platformAdmin.detailsLink')}
                </Text>
              </Box>
            </Stack>
          ))}

          {pageItems.length > 0 && (
            <ListPagination
              label={paginationLabel}
              page={page}
              totalPages={totalPages}
              onPageChange={setPage}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              bordered={false}
            />
          )}
        </Stack>
      </Stack>
    </Container>
  );
};
